import { useState, useEffect, type ReactNode } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Icon, useToast } from "@/components/ui";
import Button from "@/components/ui/Button";
import ribbon1Svg from "@/assets/shapes/ribbon-1.svg";
import ribbon2Svg from "@/assets/shapes/ribbon-2.svg";

interface CabalData {
    name: string;
    ticker: string;
    slug: string;
    lore?: string;
    imageUrl?: string;
    depositTarget: number;
    depositCurrent: number;
    leaderDeposit: number;
    inviteCode: string;
    status: string;
}

/* ════════════════════════════════════════════
   Cabal Initiate Success Page
   ════════════════════════════════════════════ */
export default function CabalSuccessPage(): ReactNode {
    const navigate = useNavigate();
    const location = useLocation();
    const { toast } = useToast();
    const cabal = (location.state as { cabal?: CabalData })?.cabal;

    const [copied, setCopied] = useState(false);

    if (!cabal) {
        return (
            <div className="relative flex min-h-[100dvh] w-full flex-col overflow-hidden max-w-[430px] mx-auto bg-[#0a0a0a]">
                <div className="flex-1 flex items-center justify-center">
                    <p className="text-[rgba(255,255,255,0.4)] font-medium">No Codev data found</p>
                </div>
            </div>
        );
    }

    const progress = cabal.depositTarget > 0
        ? Math.min((cabal.depositCurrent / cabal.depositTarget) * 100, 100)
        : 0;

    const inviteUrl = `${window.location.origin}/codevs/join/${cabal.inviteCode}`;

    async function handleShare() {
        const isMobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
        if (isMobile && navigator.share) {
            try {
                await navigator.share({
                    title: `Lets CODEV ${cabal!.ticker}`,
                    text: `Join me for CODEVing ${cabal!.ticker}!`,
                    url: inviteUrl,
                });
            } catch { /* user cancelled */ }
        } else {
            try {
                await navigator.clipboard.writeText(inviteUrl);
                setCopied(true);
                setTimeout(() => setCopied(false), 2000);
                toast("Invite link copied to clipboard!", "success");
            } catch {
                toast("Failed to copy link", "error");
            }
        }
    }

    return (
        <div className="relative flex min-h-[100dvh] w-full flex-col overflow-hidden max-w-[430px] mx-auto bg-[#0a0a0a]">
            {/* ─── Orange gradient blur (top-left) ─── */}
            <div
                aria-hidden
                className="absolute left-0 top-0 w-[442px] h-[172px] opacity-[0.89] blur-[192px]"
                style={{ background: "linear-gradient(to bottom, #ff9c58, rgba(255,156,88,0))" }}
            />

            {/* ─── Decorative ribbons (top-right) ─── */}
            <div
                aria-hidden
                className="absolute right-[-26px] top-[-106px] w-[210px] h-[313px] flex items-center justify-center"
                style={{ transform: "rotate(-167.3deg) scaleY(-1)" }}
            >
                <img src={ribbon1Svg} alt="" className="w-[150px] h-[287px]" />
            </div>
            <div
                aria-hidden
                className="absolute right-[-86px] top-[-86px] w-[211px] h-[314px] flex items-center justify-center"
                style={{ transform: "rotate(-166.96deg) scaleY(-1)" }}
            >
                <img src={ribbon2Svg} alt="" className="w-[150px] h-[287px]" />
            </div>

            {/* ─── Back arrow ─── */}
            <button
                onClick={() => navigate("/")}
                className="absolute left-[24px] top-[clamp(28px,3.3dvh,31px)] z-20 size-[32px] flex items-center justify-center"
            >
                <Icon name="back-arrow" size={32} />
            </button>

            <main className="relative z-10 flex-1 flex flex-col">
                {/* ─── Header text ─── */}
                <div className="flex flex-col gap-[6px] px-[24px] mt-[clamp(76px,9dvh,84px)]">
                    <h1 className="font-bold text-[clamp(34px,4.5dvh,42px)] leading-[1.15] text-white tracking-[-0.84px]">
                        Codev Initiated
                    </h1>
                    <p className="font-medium text-[clamp(15px,1.9dvh,18px)] leading-[1.15] text-white opacity-50">
                        Invite Your frens
                    </p>
                </div>

                {/* ─── Success Card ─── */}
                <div className="relative w-full px-[16px] mt-[clamp(36px,5dvh,50px)]">
                    {/* Token avatar (overlapping card top, centered) */}
                    <div className="relative z-10 flex justify-center mb-[-50px]">
                        <div className="bg-gradient-to-b from-[#2e241d] to-[#1e1b19] rounded-full p-[8px]">
                            <div className="size-[84px] rounded-full overflow-hidden">
                                {cabal.imageUrl ? (
                                    <img
                                        alt={cabal.ticker}
                                        className="w-full h-full object-contain"
                                        src={cabal.imageUrl}
                                    />
                                ) : (
                                    <div className="w-full h-full bg-[#0a0a0a] flex items-center justify-center">
                                        <span className="text-[28px] font-extrabold text-white">
                                            {cabal.ticker.toUpperCase().charAt(0)}
                                        </span>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Card */}
                    <div
                        className="relative w-full rounded-[16px] overflow-hidden"
                        style={{
                            border: "0.5px solid transparent",
                            backgroundImage: "linear-gradient(to bottom, #171717, #171717), linear-gradient(to bottom, rgba(153,153,153,0.5), rgba(153,153,153,0))",
                            backgroundOrigin: "border-box",
                            backgroundClip: "padding-box, border-box",
                        }}
                    >
                        {/* Blurred token image glow behind card */}
                        {cabal.imageUrl && (
                            <img
                                src={cabal.imageUrl}
                                alt=""
                                aria-hidden
                                className="absolute left-[3%] top-[20%] w-[94%] h-[80%] object-cover opacity-60 blur-[40px] pointer-events-none"
                            />
                        )}

                        {/* Inner shadow overlay */}
                        <div aria-hidden className="absolute inset-0 rounded-[inherit] pointer-events-none shadow-[inset_0px_0px_14px_0px_rgba(228,91,0,0.15)]" />

                        {/* Card content */}
                        <div className="relative flex flex-col gap-[20px] pt-[62px] px-[17px] pb-[20px]">
                            {/* Ticker + Lore + LIVE badge */}
                            <div className="flex gap-[16px] items-start w-full">
                                <div className="flex-1 flex flex-col gap-[12px] min-w-0 leading-[1.15]">
                                    <p className="font-extrabold text-[clamp(26px,3.4dvh,32px)] text-white tracking-[-0.84px] truncate">
                                        {cabal.ticker.toUpperCase()}
                                    </p>
                                    <p className="font-medium text-[14px] text-[rgba(255,255,255,0.7)] tracking-[-0.28px] truncate">
                                        {cabal.lore || cabal.name}
                                    </p>
                                </div>
                                <div className="bg-[rgba(255,255,255,0.15)] flex items-center justify-center px-[12px] py-[8px] rounded-[8px] shrink-0">
                                    <span className="font-semibold text-[13px] text-white leading-normal">
                                        LIVE
                                    </span>
                                </div>
                            </div>

                            {/* Divider */}
                            <div className="w-full h-px bg-[rgba(255,255,255,0.1)]" />

                            {/* Amount Filled + Time Left */}
                            <div className="flex flex-col gap-[20px] items-start w-full">
                                <div className="flex gap-[16px] items-center w-full font-medium leading-[1.15]">
                                    <div className="flex-1 flex flex-col gap-[6px] min-w-0">
                                        <p className="text-[14px] text-[rgba(255,255,255,0.5)] tracking-[-0.28px]">
                                            Amount Filled
                                        </p>
                                        <p className="text-[clamp(18px,2.1dvh,20px)] text-white tracking-[-0.4px]">
                                            {parseFloat(cabal.depositCurrent.toFixed(2))} / {parseFloat(cabal.depositTarget.toFixed(2))} SOL
                                        </p>
                                    </div>
                                    <div className="flex-1 flex flex-col gap-[6px] items-end min-w-0">
                                        <p className="text-[14px] text-[rgba(255,255,255,0.5)] tracking-[-0.28px]">
                                            Time Left
                                        </p>
                                        <CountdownTimer />
                                    </div>
                                </div>

                                {/* Progress bar */}
                                <div className="w-full h-[14px] bg-[rgba(255,255,255,0.06)] rounded-[50px] overflow-hidden">
                                    <div
                                        className="h-full bg-white rounded-[50px] transition-all duration-500"
                                        style={{ width: `${Math.max(progress, 2)}%` }}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* ─── Bottom: Description + Share CTA ─── */}
                <div className="mt-auto flex flex-col gap-[24px] items-center px-[20px] pb-[clamp(36px,4.7dvh,44px)]">
                    <p className="font-medium text-[16px] text-[rgba(255,255,255,0.4)] text-center tracking-[-0.32px] leading-[1.23] max-w-[259px]">
                        Invite frens to join your launch pool
                    </p>
                    <Button
                        variant="cta"
                        size="lg"
                        textStyle="v2"
                        fullWidth
                        onClick={handleShare}
                        className="text-[clamp(18px,2.1dvh,20px)]"
                    >
                        <Icon name="share" size={24} />
                        {copied ? "Link Copied!" : "Share Link"}
                    </Button>
                </div>
            </main>
        </div>
    );
}

/* ─── Countdown Timer ─── */
function CountdownTimer(): ReactNode {
    const [seconds, setSeconds] = useState(59 * 60 + 10);

    useEffect(() => {
        const interval = setInterval(() => {
            setSeconds((s) => (s > 0 ? s - 1 : 0));
        }, 1000);
        return () => clearInterval(interval);
    }, []);

    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;
    const label = `${h > 0 ? String(h) + "h " : ""}${String(m)}m ${String(s)}s`;

    return (
        <p className="font-medium text-[clamp(18px,2.1dvh,20px)] text-white tracking-[-0.4px] text-right leading-[1.15]">
            {label}
        </p>
    );
}
